(ns io.daskul.webapp.ui.theme
  "Single source of visual truth for every product on the framework.

   Products override ONLY the palette (and optionally the default density).
   No component may hardcode a size, weight, radius or colour — if a value is
   not here, it does not exist. See ADR F-060.

   Usage:
     (theme/create {})                                  ; :default density
     (theme/create {:density-key :compact})
     (theme/create {:density-key :comfortable :primary tenant-shades})

   Lives in lib/webapp-ui, not lib/framework: lib/framework is JVM-only and is
   not on either webapp's cljs classpath. This namespace is, via the
   `../../../lib/webapp-ui/src` entry in each product webapp's deps.edn."
  (:require ["@mantine/core" :refer [createTheme]]))

;; ---------------------------------------------------------------------------
;; Palette — roles, not names. From the pipeline-revamp tokens.
;;
;;   brand   primary action        (the only fill for a primary button)
;;   accent  attention only        (NEVER a button fill)
;;   red     destructive / alert
;;   green   settled success       (NEVER a button fill)
;;   sand    surfaces, borders, text
;;
;; The design handoff named these `violet` and `amber`. They keep their
;; existing `brand` / `accent` keys because Mantine treats an unknown colour
;; name as a raw CSS colour rather than falling back — and `"brand"` has 17
;; call sites plus `fsm/colors.cljs`'s default for every unmapped variant.
;; The role names live in this comment; the keys stay compatible.
;;
;; #795 put the brand-kit logo on screen; #798 moved the ramps under it. Both
;; were Tailwind's (violet, amber) — the right family, the wrong hue, so the
;; kit mark sat next to UI chrome one or two degrees off it.
;; ---------------------------------------------------------------------------

;; Three of these ten shades ARE kit colours, at their natural place on the
;; ramp — the kit's own purples differ only in lightness (hue 259.8–261.4,
;; near-flat), so the ramp holds one hue rather than drifting bluer at the
;; pale end the way Tailwind's violet does:
;;
;;   5  #9462ee  Klei Purple Light   — the kit's tint, for dark grounds
;;   6  #7536e9  Klei Purple         — :primaryShade, every primary fill
;;   9  #3f1c86  the deep end of the kit login gradient
;;
;; The other seven keep Tailwind violet's lightness steps, re-anchored on
;; shade 5 and interpolated 6 → 9, because that ramp's spacing is well tuned
;; and nothing in the kit contradicts it. Saturation follows the kit's ~0.80
;; through the middle instead of Tailwind's 0.83–0.95.
;;
;; The login gradient is `brand-6 → brand-9` in CSS (both products), so it is
;; these two values and not a second copy of them.
(def brand
  ["#f6f1ff" "#efe7fe" "#e3d5fd" "#ceb5fb" "#b28df6"
   "#9462ee" "#7536e9" "#5f1ed7" "#511ead" "#3f1c86"])

;; Klei Orange at shade 5, same construction: Tailwind amber's lightness steps
;; with the kit's hue (−2.6°) and saturation.
;;
;; Shade 5 is the kit colour and CARRIES NO TEXT — #f29512 on white measures
;; 2.31:1, which the kit calls out and which `--warning-fg` avoids by being
;; shade 7 (5.00:1 on shade 0). Nor is accent ever a button fill; note 3 at the
;; bottom of this file is the only thing enforcing either.
(def accent
  ["#fffbef" "#fdf1cc" "#fce18f" "#facc53" "#f8b62b"
   "#f29512" "#da7009" "#b54e0c" "#943c10" "#7a3211"])

(def red
  ["#fbf5f5" "#fee2e2" "#eccccc" "#f3c0c0" "#f87171"
   "#ef4444" "#dc2626" "#b91c1c" "#991b1b" "#7f1d1d"])

(def green
  ["#f3faf4" "#dcf3e0" "#c8e6cd" "#86cf96" "#4ade80"
   "#22c55e" "#16a34a" "#15803d" "#166534" "#14532d"])

;; Steps 0–2 are the paper and keep their warmth. Steps 3–5 are borders, cooled
;; in #683 so they don't read as the warmest thing on screen once the text
;; roles cooled (R−B drops from 19–23 to 10–13).
(def sand
  ["#fdfcfa" "#faf8f5" "#f6f4f0" "#f0ece6" "#e8e4dc"
   "#d7d3ca" "#b0a99e" "#857f76" "#625c54" "#2b2b2b"])

;; Carried over unchanged — `fsm/colors.cljs` maps the `info` and `warning`
;; variants onto these, so they are load-bearing, not decoration.
(def blue
  ["#e7f5ff" "#d0ebff" "#a5d8ff" "#74c0fc" "#4dabf7"
   "#339af0" "#228be6" "#1c7ed6" "#1971c2" "#1864ab"])

(def yellow
  ["#fff9db" "#fff3bf" "#ffec99" "#ffe066" "#ffd43b"
   "#fcc419" "#fab005" "#f59f00" "#f08c00" "#e67700"])


;; ---------------------------------------------------------------------------
;; Type roles — NINE. Anything not listed reuses the nearest role.
;; Sora is for NAMES OF THINGS only: record titles, section titles, eyebrows,
;; tab labels. Never body, never a field value, never helper text.
;; ---------------------------------------------------------------------------

(def font-heading "Sora, sans-serif")
(def font-body    "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif")

;; Three steps. `:default` is what ships; `:compact` is for admins processing
;; fifty records a day on a large monitor, `:comfortable` for occasional users,
;; laptops and projectors. The spread is deliberately narrow — ~12% total
;; height — because the hierarchy is positional, not size-driven.
;;
;; WIRED vs TARGET:
;;
;;   wired   :label (InputWrapper), :helper (description/error),
;;           :table-cell :size, :mantine-size, :input :radius,
;;           and since #675 the :input / :button :height — see
;;           `mantine-scale-overrides`
;;   target  :record-title / :section-title / :eyebrow / :body — the Sora
;;           roles have no consumer until the primitives land (steps 3–7),
;;           which is expected, not a defect
;;
;; :button :size and :table-cell :row-height are still inert. Neither is
;; load-bearing yet; wire them when something needs to align against them.
(def density
  "What MANTINE needs in JS, per density step. Everything else — gutters,
   gaps, the type scale, control heights — lives in lib/webapp-ui/css/tokens
   and is scoped by [data-density] there (#684).

   Keeping the pixel values in both places was the whole problem: a JS-emitted
   :root variable cannot be overridden by a media query, so the 46px
   touch-target rule could never have reached them.

   :mantine-size selects the slot the stylesheets override — compact drives
   Mantine's `xs`, default `sm`, comfortable `md` — so these two keys and
   spacing.css must be changed together."
  {:compact     {:mantine-size "xs" :radius 7}
   :default     {:mantine-size "sm" :radius 8}
   :comfortable {:mantine-size "md" :radius 9}})

;; The record title barely moves across densities (20/21/22): it is the only
;; thing on the page that says which record you are in.

;; ---------------------------------------------------------------------------
;; Framework constants — these THROW, they are not conventions (F-060).
;; ---------------------------------------------------------------------------

(def limits
  {:max-tabs               5   ; then overflow into "More"
   :max-attention-banners  2   ; then "N things need attention"
   :max-inline-collections 3   ; then push to the Related tab
   :max-header-buttons     2   ; then push into the ⋯ menu
   :max-pinned-facts       4})

;; ---------------------------------------------------------------------------
;; Mantine theme
;; ---------------------------------------------------------------------------

(def ^:private named-radius
  "The named scale, kept so call sites that pass `:radius \"xl\"` by hand keep
   the value they render today. New code should take the default instead of
   naming a step — the numbers belong here, not at the call site."
  {:xs "0.375rem"
   :sm "0.5rem"
   :md "0.75rem"
   :lg "1rem"
   :xl "1.5rem"})

(defn- component-overrides
  "The label demotion is the highest-leverage line in this file: it fixes the
   section-vs-label collision (F-060) on every existing form, with no component
   work at all. Ship it before anything else.

   Measured on the Lead edit page before this landed: section titles rendered
   at 14px/600 and field labels at 14px/500 — the SAME size, one weight step
   apart. (F-060 records the titles as 16px/600; they are not.)"
  [d]
  (let [{:keys [radius mantine-size]} d
        input-props {:size mantine-size :radius radius}]
    ;; Every value below that a stylesheet needs to move is a var(), not a
    ;; baked number (#684). Mantine writes these into its own style block, so
    ;; a literal here could not be overridden by breakpoints.css — and the
    ;; label has to shrink on a phone along with everything else.
    {:InputWrapper {:styles {:label       {:fontSize     "var(--label-size)"
                                           :fontWeight   "var(--label-weight)"
                                           :color        "var(--text-muted)"
                                           :marginBottom "var(--label-gap)"}
                             :description {:fontSize "var(--helper-size)"
                                           :color    "var(--text-faint)"
                                           :marginTop 5}
                             :error       {:fontSize "var(--helper-size)"}}}

     :TextInput       {:defaultProps input-props}
     :Textarea        {:defaultProps input-props}
     :NumberInput     {:defaultProps input-props}
     :Select          {:defaultProps input-props}
     :MultiSelect     {:defaultProps input-props}
     :DatePickerInput {:defaultProps input-props}
     :TimeInput       {:defaultProps input-props}
     ;; Not in the handoff's list, but in use here — PasswordInput sits beside
     ;; TextInput on the login form and DateInput beside DatePickerInput, so
     ;; leaving them at Mantine's default size would split those forms in two.
     :PasswordInput   {:defaultProps input-props}
     :DateInput       {:defaultProps input-props}
     :Autocomplete    {:defaultProps input-props}

     :Button       {:defaultProps input-props}
     :ActionIcon   {:defaultProps input-props}

     ;; Text/Anchor had no themed size, so components passed `:size` on nearly
     ;; every call (229 of ~252 Text sites) just to reach the body size. Mantine's
     ;; own default is `md` (16px) — a step ABOVE the system body — so the bare
     ;; sites rendered slightly oversized. Default both to the density size (`sm`
     ;; at :default), so a call names a size only for real emphasis.
     :Text         {:defaultProps {:size mantine-size}}
     :Anchor       {:defaultProps {:size mantine-size}}

     ;; Both spacings follow the density scalar. The handoff pinned horizontal
     ;; to "md" (16px) regardless of density — measured on the leads list, that
     ;; clips the "Contacted" state badge to 66px of the 71px it needs, and it
     ;; contradicts what :compact means. Density drives both, or neither.
     :Table        {:defaultProps {:verticalSpacing mantine-size
                                   :horizontalSpacing mantine-size}
                    :styles {:th {:fontSize   "calc(var(--table-cell-size) - 1px)"
                                  :fontWeight 600
                                  :color      "var(--text-muted)"}
                             :td {:fontSize "var(--table-cell-size)"}}}

     ;; Alert = attention banners (F-062). No right-aligned action slot exists;
     ;; put buttons in children with a Group.
     :Alert        {:defaultProps {:radius "md" :variant "light"}
                    :styles {:title {:fontSize 14 :fontWeight 700}
                             :message {:fontSize 13 :lineHeight 1.55}}}

     ;; Tab COLOUR is in colors.css, not here. `:styles` becomes an inline
     ;; style, and an inline style beats any stylesheet rule — so setting the
     ;; resting colour here made the selected tab unstylable, since its state
     ;; is a [data-active] attribute that only a selector can reach.
     :Tabs         {:styles {:tab {:fontSize 13.5}}}
     :Badge        {:defaultProps {:radius "xl"}}
     :Drawer       {:defaultProps {:position "right" :size 460 :offset 0}}
     :Popover      {:defaultProps {:radius "md" :shadow "lg" :withinPortal true}}
     :Tooltip      {:defaultProps {:radius "sm" :openDelay 300}}
     ;; A transition in the ⋯ menu is a real action with a `from → to`
     ;; subtitle under it, not a one-word command, so the dropdown is sized
     ;; for two lines of text and a comfortable target rather than the
     ;; content-width default it was collapsing to.
     :Menu         {:defaultProps {:radius "md" :shadow "md" :width 240}
                    :styles {:dropdown {:padding 6}
                             :item {:fontSize 14 :padding "9px 12px"}}}}))

(defn create
  "Build the Mantine theme.

   opts:
     :density-key  :compact | :default (shipped) | :comfortable
     :primary      10-shade vector — products may override the accent only"
  [{:keys [density-key primary] :or {density-key :default}}]
  (let [d (or (get density density-key)
              (throw (ex-info "Unknown density" {:density-key density-key
                                                 :known (set (keys density))})))]
    (createTheme
     (clj->js
      {:primaryColor  "brand"
       :primaryShade  6                    ; #7536e9 — Klei Purple
       :fontFamily    font-body
       :headings      {:fontFamily font-heading
                       :fontWeight "700"}
       :radius        named-radius
       :defaultRadius (:radius d)
       :colors        {:brand  (or primary brand)
                       :accent accent
                       :red    red
                       :green  green
                       :blue   blue
                       :yellow yellow
                       :sand   sand}
       :components    (component-overrides d)
       ;; :tokens and :semantic are gone — a primitive reads a token with
       ;; var(--gutter), not by reaching into the theme object. :limits stays:
       ;; the caps are framework constants that THROW, and that check is code.
       :other         {:density (name density-key)
                       :limits  limits}}))))

;; ---------------------------------------------------------------------------
;; Notes for implementers
;;
;; 1. Density is a THEME scalar, never per-screen. A screen never picks its own.
;;    Read it via (-> theme .-other .-density) if a primitive truly must branch.
;;
;; 2. Mantine's `disabled` buttons swallow pointer events, so a Tooltip on a
;;    blocked transition (F-061) needs `data-disabled` + preventDefault, not the
;;    `disabled` prop.
;;
;; 3. Accent and green are never button fills. Enforced by convention here;
;;    worth a lint rule if it starts slipping.
;;
;; 4. Compact vs comfortable changes SIZE AND SPACING ONLY — no colour differs
;;    between them, so flipping density never requires re-checking contrast.
;; ---------------------------------------------------------------------------
